import React, { ReactNode, useEffect, useState } from 'react';
import { loadYaml } from '../utilis/loadYAML';
import BifurcatedPromo from '../components/BifurcatedPromo';
import VideoComp from '../components/VideoComp';
import './force.css';

// ✅ Icon imports
import { GiKevlarVest } from 'react-icons/gi';
import { MdOutlineSpeed } from 'react-icons/md';
import { FaTruckMoving } from 'react-icons/fa';
import { HiOutlineEye } from 'react-icons/hi';


const iconMap: Record<string, any> = {
  shield: GiKevlarVest,
  bolt: MdOutlineSpeed,
  truck: FaTruckMoving,
  eye: HiOutlineEye,
};


const getIcon = (name: string): React.ReactNode | null => {
  const IconComponent = iconMap[name];
  if (!IconComponent) {
    console.warn(`Icon not found for: ${name}`);
    return null;
  }
  return React.createElement(IconComponent, { className: 'feature-icon-svg' });
};

// === Interfaces ===
interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Section {
  quote: ReactNode;
  designation: any;
  name: any;
  type: string;
  id?: string;
  title?: string;
  description?: string;
  heading?: string;
  images?: string[];
  image?: string;
  video?: string;
  features?: Feature[];
}

function Force() {
  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    loadYaml('/config/force.yaml').then((data) => {
      setSections(data.sections || []);
    });
  }, []);

  return (
    <div className="mission-page">
      {sections.map((section, index) => {
        const renderHr = index !== 0 && section.type !== 'hero';

        // HERO SECTION
        if (section.type === 'hero' && section.image) {
          return (
            <section
              className="career-banner"
              style={{ backgroundImage: `url(${section.image})` }}
              key={index}
            >
              {section.title && <h1 className="career-banner-title">{section.title}</h1>}
            </section>
          );
        }

        // TEXT SECTION
        if (section.type === 'text') {
          const [boldLine, ...rest] = (section.description || '').split('---');
          const secondHalf = rest.join('---').trim();

          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section className="mission-section text-section">
                <h2>{section.title}</h2>
                <div className="text-bifurcated-container">
                  <div className="text-bifurcated-left">
                    {boldLine && <p className="bold-line">{boldLine.trim()}</p>}
                  </div>
                  <div className="text-bifurcated-right">
                    {secondHalf && <p className="secondary-text">{secondHalf}</p>}
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        }

        // BIFURCATED PROMO SECTION
        if (section.type === 'bifurcatedPromo') {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <BifurcatedPromo
                id={`section-${index}`}
                title={section.title ?? ''}
                heading={section.heading ?? ''}
                description={section.description ?? ''}
                images={section.images ?? []}
              />
            </React.Fragment>
          );
        }

        // FEATURE GRID SECTION
        if (section.type === 'featureGrid' && section.features) {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section className="section feature-grid-section">
                {section.title && <h2 className="feature-grid-title">{section.title}</h2>}
                <div className="feature-grid">
                  {section.features.map((feature, i) => (
                    <div className="feature-box" key={i}>
                      <div className="feature-icon">{getIcon(feature.icon)}</div>
                      <h3 className="feature-title">{feature.title}</h3>
                      <p className="feature-description">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </React.Fragment>
          );
        }

        // VIDEO PROMO SECTION
        if (section.type === 'videoPromo' && section.video) {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <VideoComp
                id={section.id || `video-section-${index}`}
                title={section.title || ''}
                heading={section.heading || ''}
                description={section.description || ''}
                video={section.video}
              />
            </React.Fragment>
          );
        }

  // TESTIMONY SECTION
 if (section.type === 'testimony') {
   const quoteStr = String(section.quote ?? '');
   const [boldLine, ...rest] = quoteStr.split('---');
   const secondHalf = rest.join('---').trim();
 
   return (
     <React.Fragment key={index}>
       {renderHr && <hr className="section-divider" />}
       <section
         className="mission-section text-section"
         id={section.id || `section-${index}`}
       >
         <h2>{section.title || 'Testimonial'}</h2>
         <div className="text-bifurcated-container">
           <div className="text-bifurcated-left">
             {boldLine && <p className="bold-line">“{boldLine.trim()}”</p>}
           </div>
           <div className="text-bifurcated-right">
             {secondHalf && <p className="secondary-text">{secondHalf}</p>}
             <p className="secondary-text" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
               — {section.name}, {section.designation}
             </p>
           </div>
         </div>
       </section>
     </React.Fragment>
   );
 }
        // Unknown section
        console.warn(`Unknown section type: ${section.type}`);
        return null;
      })}
    </div>
  );
}

export default Force;
